import {
  Card, Table, Row, Col, Descriptions, Tag, Divider, Space,
  Button, Select, Input, Form, Image, Avatar, Typography, Badge, Tooltip, Empty
} from "antd";
import {
  SearchOutlined, UserOutlined, CarOutlined, EnvironmentOutlined,
  DollarOutlined, PlusCircleOutlined, CheckCircleOutlined, CloseCircleOutlined
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { ProductServices } from "../Services/ProductServices";
import { notify } from "../customs/NotifyProvider";
import { ENDPOINTS } from "../Services/ENDPOINTS";
import { AddonServices } from "../Services/AddonServices";

const { Text, Title } = Typography;

/* ─── helpers ─── */
const safeJSON = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try { return JSON.parse(value); } catch { return fallback; }
};

const parseAddons = (addonsRaw) => {
  if (!addonsRaw) return [];
  try {
    const fixed = addonsRaw.replace(/^\{/, "[").replace(/\}$/, "]");
    const parsed = JSON.parse(fixed);
    return parsed.map(item => typeof item === "string" ? JSON.parse(item) : item);
  } catch { return []; }
};

const fmt = (n) => (n != null && n !== "") ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

const STATUS_BADGE = {
  Pending:   "warning",
  Accepted:  "success",
  Completed: "processing",
  Cancelled: "error",
};

const TYPE_COLOR = {
  rental:     "geekblue",
  airport:    "cyan",
  outstation: "volcano",
};

/* ════════════════════════════════════════════ */
const OrderPage = () => {
  const [orders, setOrders]               = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [drivers, setDriver]              = useState([]);
  const [searchText, setSearchText]       = useState("");
  const [serviceFilter, setServiceFilter] = useState("All");

  const getAllOrder = async () => {
    try {
      const res = await ProductServices.getAllOrders();
      const data = res.data || [];
      setOrders(data);
      setSelectedOrder(data[0] || null);
    } catch (err) { console.error(err); }
  };

  const handleAcceptOrder = async (status) => {
    const response = await fetch(`${ENDPOINTS.ORDERS_UPDATE}/${selectedOrder.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (response.ok) notify.success("Order Updated");
    else notify.warning("Please try again");
    getAllOrder();
  };

  const handleAssignDriver = async (driverData) => {
    const response = await fetch(`${ENDPOINTS.ORDERS_UPDATE}/${selectedOrder.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ asign_driver: driverData }),
    });
    if (response.ok) notify.success("Driver Assigned");
    else notify.warning("Please try again");
    getAllOrder();
  };

  const getAddonServices = async () => {
    try {
      const request = await AddonServices.getAllDriver();
      setDriver(
        (request.data || [])
          .filter(d => d.status === "active")
          .map(d => ({
            label: `${d.driver_name} (${d.mobile})`,
            value: d.id,
            fullData: d,
          }))
      );
    } catch (err) { console.error(err); }
  };

  useEffect(() => { getAllOrder(); getAddonServices(); }, []);

  /* latest first */
  const filteredOrders = [...orders]
    .filter((order) => {
      const user = safeJSON(order.user, {});
      const q    = searchText.toLowerCase();
      const matchesSearch =
        !searchText ||
        String(order.id).includes(q) ||
        user.fullName?.toLowerCase().includes(q) ||
        user.mobile?.includes(q);
      const matchesService =
        serviceFilter === "All" || order.type === serviceFilter;
      return matchesSearch && matchesService;
    })
    .sort((a, b) => b.id - a.id);   // ← latest first

  /* ── table columns ── */
  const columns = [
    {
      title: "S.No",
      width: 55,
      align: "center",
      render: (_, __, i) => <Text type="secondary">{i + 1}</Text>,
    },
    {
      title: "Customer",
      render: (_, r) => {
        const user = safeJSON(r.user, {});
        return (
          <Space>
            <Avatar size={34} icon={<UserOutlined />} style={{ background: "#4f46e5", flexShrink: 0 }}>
              {user.fullName?.[0]?.toUpperCase()}
            </Avatar>
            <div>
              <Text strong style={{ display: "block", fontSize: 13 }}>
                {user.fullName || "—"}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {user.mobile || ""}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: "Trip Type",
      dataIndex: "type",
      align: "center",
      width: 120,
      render: (t) => (
        <Tag color={TYPE_COLOR[t] || "default"} style={{ borderRadius: 12 }}>
          {t?.toUpperCase() || "—"}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      width: 140,
      render: (s) => (
        <Badge
          status={STATUS_BADGE[s] || "default"}
          text={<Text style={{ fontSize: 12, fontWeight: 500 }}>{s || "Unknown"}</Text>}
        />
      ),
    },
    {
      title: "Paid",
      align: "center",
      width: 100,
      render: (_, r) => {
        const p    = safeJSON(r.pay_type, {});
        const paid = Number(p.pay_amount || 0);
        return paid > 0
          ? <Text strong style={{ color: "#16a34a" }}>{fmt(paid)}</Text>
          : <Text type="secondary">—</Text>;
      },
    },
  ];

  /* ── detail panel ── */
  const renderDetails = () => {
    if (!selectedOrder) return (
      <Empty description="Select an order to view details" style={{ padding: "40px 0" }} />
    );

    const user    = safeJSON(selectedOrder.user,     {});
    const trip    = safeJSON(selectedOrder.trip,     {});
    const car     = safeJSON(selectedOrder.car,      {});
    const pricing = safeJSON(selectedOrder.pricing,  {});
    const payment = safeJSON(selectedOrder.pay_type, {});
    const addons  = parseAddons(selectedOrder.addons);

    const paidAmt    = Number(payment.pay_amount  || 0);
    const finalPrice = Number(pricing.finalPrice  || 0);
    const remaining  = finalPrice - paidAmt;

    return (
      <Space direction="vertical" style={{ width: "100%" }} size="middle">

        {/* ── Customer ── */}
        <Card
          size="small"
          title={<Space><UserOutlined style={{ color: "#4f46e5" }} /><span>Customer Details</span></Space>}
          headStyle={{ background: "#f5f5ff", borderBottom: "1px solid #e0e0f8" }}
        >
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Full Name">{user.fullName || "—"}</Descriptions.Item>
            <Descriptions.Item label="Mobile">
              {user.countryCode ? `${user.countryCode} ` : ""}{user.mobile || "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Email" span={2}>{user.email || "—"}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* ── Trip ── */}
        <Card
          size="small"
          title={<Space><EnvironmentOutlined style={{ color: "#0891b2" }} /><span>Trip Details</span></Space>}
          headStyle={{ background: "#f0faff", borderBottom: "1px solid #bae6fd" }}
        >
          <Descriptions bordered size="small" column={2}>
            {trip.type && (
              <Descriptions.Item label="Trip Type">
                <Tag color="purple">{trip.type.toUpperCase()}</Tag>
              </Descriptions.Item>
            )}
            {trip.droptype && (
              <Descriptions.Item label="Drop Type">
                <Tag color="blue">{trip.droptype.toUpperCase()}</Tag>
              </Descriptions.Item>
            )}
            {trip.from && <Descriptions.Item label="From">{trip.from}</Descriptions.Item>}
            {trip.to   && <Descriptions.Item label="To">{trip.to}</Descriptions.Item>}
            {trip.total_distance && (
              <Descriptions.Item label="Distance">
                {trip.droptype === "premium" ? trip.total_distance * 2 : trip.total_distance} km
              </Descriptions.Item>
            )}
            {trip.total_distance_km && (
              <Descriptions.Item label="Distance">{trip.total_distance_km} km</Descriptions.Item>
            )}
            {(trip.pickup_date || trip.pickup_time) && (
              <Descriptions.Item label="Pickup">
                {trip.pickup_date}{trip.pickup_time ? `, ${trip.pickup_time}` : ""}
              </Descriptions.Item>
            )}
            {trip.rental_duration && (
              <Descriptions.Item label="Rental Duration">{trip.rental_duration} hrs</Descriptions.Item>
            )}
            {trip.totalDay && (
              <Descriptions.Item label="Rental Days">{trip.totalDay} days</Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* ── Car ── */}
        <Card
          size="small"
          title={<Space><CarOutlined style={{ color: "#d97706" }} /><span>Car Details</span></Space>}
          headStyle={{ background: "#fffbeb", borderBottom: "1px solid #fde68a" }}
          extra={
            car.upload_img && (
              <Image
                src={`${ENDPOINTS.IMAGE_BASE}${car.upload_img}`}
                height={45}
                style={{ borderRadius: 6, objectFit: "cover" }}
                preview={false}
              />
            )
          }
        >
          <Descriptions bordered size="small" column={2}>
            {car.car_name && <Descriptions.Item label="Car Name">{car.car_name}</Descriptions.Item>}
            {car.car_type && <Descriptions.Item label="Type">{car.car_type}</Descriptions.Item>}
            {car.rating   && <Descriptions.Item label="Rating">⭐ {car.rating}</Descriptions.Item>}
            {car.status   && (
              <Descriptions.Item label="Status">
                <Tag color="success">{car.status}</Tag>
              </Descriptions.Item>
            )}
            {car.display_price != null && (
              <Descriptions.Item label="Base Price">{fmt(car.display_price)}</Descriptions.Item>
            )}
            {car?.calculation?.total_driver_bata != null && (
              <Descriptions.Item label="Driver Bata">{fmt(car.calculation.total_driver_bata)}</Descriptions.Item>
            )}
            {car.additional_travel_amount != null && (
              <Descriptions.Item label="Add. Per KM">{fmt(car.additional_travel_amount)}</Descriptions.Item>
            )}
            {car.additional_travel_amount_per_hr != null && (
              <Descriptions.Item label="Add. Per Hour">{fmt(car.additional_travel_amount_per_hr)}</Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* ── Add-ons ── */}
        <Card
          size="small"
          title={<Space><PlusCircleOutlined style={{ color: "#7c3aed" }} /><span>Add-ons</span></Space>}
          headStyle={{ background: "#faf5ff", borderBottom: "1px solid #e9d5ff" }}
        >
          {addons.length === 0 ? (
            <Text type="secondary">No add-ons selected</Text>
          ) : (
            <Descriptions bordered size="small" column={2}>
              {addons.map((a, i) => (
                <Descriptions.Item key={i} label={a.name}>{fmt(a.price)}</Descriptions.Item>
              ))}
            </Descriptions>
          )}
        </Card>

        {/* ── Pricing ── */}
        <Card
          size="small"
          title={
            <Space>
              <DollarOutlined style={{ color: "#16a34a" }} />
              <span>Pricing Summary</span>
              {selectedOrder.utr_id && (
                <Tag color="red">UTR: {selectedOrder.utr_id}</Tag>
              )}
            </Space>
          }
          headStyle={{ background: "#f0fdf4", borderBottom: "1px solid #bbf7d0" }}
        >
          <Descriptions bordered size="small" column={2}>
            {car?.calculation?.extra_km > 0 && (
              <Descriptions.Item label={`Extra KM (${car.calculation.extra_km} km)`}>
                {fmt(car.calculation.extra_km_charge)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Original Price">{fmt(pricing.originalPrice)}</Descriptions.Item>
            <Descriptions.Item label="Discounted Price">{fmt(pricing.discountedPrice)}</Descriptions.Item>
            <Descriptions.Item label="Add-ons Total">{fmt(pricing.totalAddons)}</Descriptions.Item>
            <Descriptions.Item label="Coupon Discount">{fmt(pricing.couponDiscount)}</Descriptions.Item>
            <Descriptions.Item label="Final Price">
              <Text strong style={{ fontSize: 14 }}>{fmt(pricing.finalPrice)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Paid Amount">
              <Text strong style={{ color: "#16a34a" }}>{fmt(paidAmt)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Remaining">
              {remaining <= 0
                ? <Tag color="success" icon={<CheckCircleOutlined />}>Fully Paid</Tag>
                : <Tag color="error"   icon={<CloseCircleOutlined />}>{fmt(remaining)}</Tag>
              }
            </Descriptions.Item>
          </Descriptions>
        </Card>

      </Space>
    );
  };

  /* ── assigned driver ── */
  const renderDriver = () => {
    if (!selectedOrder?.asign_driver) return (
      <Text type="secondary">No driver assigned yet</Text>
    );
    const d = safeJSON(selectedOrder.asign_driver, {});
    return (
      <Descriptions size="small" column={3}>
        {/* {d.driver_name && <Descriptions.Item label="Name">{d.driver_name}</Descriptions.Item>} */}
        {d.mobile      && <Descriptions.Item label="Mobile">{d.mobile}</Descriptions.Item>}
        {d.license_no  && <Descriptions.Item label="License">{d.license_no}</Descriptions.Item>}
        {d.experience  && <Descriptions.Item label="Experience">{d.experience} yrs</Descriptions.Item>}
      </Descriptions>
    );
  };

  /* ─── page ─── */
  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh" }}>

      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0 }}>Order Management</Title>
        <Text type="secondary">View and manage all customer bookings</Text>
      </div>

      <Row gutter={20} align="top">

        {/* ══ LEFT — Order List ══ */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>All Orders</span>
                <Tag color="blue">{filteredOrders.length}</Tag>
              </Space>
            }
            bodyStyle={{ padding: 0 }}
            style={{ borderRadius: 8 }}
          >
            {/* Filters */}
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
              <Row gutter={10}>
                <Col flex="1">
                  <Input
                    placeholder="Search by name, ID or mobile…"
                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                    value={searchText}
                    allowClear
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
                <Col>
                  <Select
                    value={serviceFilter}
                    style={{ width: 160 }}
                    onChange={setServiceFilter}
                    options={[
                      { value: "All",        label: "All Types" },
                      { value: "rental",     label: "Rental" },
                      { value: "airport",    label: "Airport" },
                      { value: "outstation", label: "Outstation" },
                    ]}
                  />
                </Col>
              </Row>
            </div>

            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredOrders}
              pagination={{ pageSize: 8, size: "small", style: { padding: "8px 16px" } }}
              size="middle"
              onRow={(record) => ({
                onClick: () => setSelectedOrder(record),
                style: {
                  cursor: "pointer",
                  background: record.id === selectedOrder?.id ? "#f0f5ff" : undefined,
                },
              })}
              rowClassName={(r) => r.id === selectedOrder?.id ? "ant-table-row-selected" : ""}
              scroll={{ x: "max-content" }}
            />
          </Card>
        </Col>

        {/* ══ RIGHT — Order Detail ══ */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>Order Details</span>
                {selectedOrder?.id && <Tag color="blue">#{selectedOrder.id}</Tag>}
                {selectedOrder?.status && (
                  <Badge
                    status={STATUS_BADGE[selectedOrder.status] || "default"}
                    text={selectedOrder.status}
                  />
                )}
              </Space>
            }
            extra={
              <Space>
                <Tooltip title="Mark as Accepted">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    style={{ background: "#16a34a", borderColor: "#16a34a" }}
                    onClick={() => handleAcceptOrder("Accepted")}
                  >
                    Accept
                  </Button>
                </Tooltip>
                <Tooltip title="Mark as Completed">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleAcceptOrder("Completed")}
                  >
                    Complete
                  </Button>
                </Tooltip>
                <Tooltip title="Cancel this order">
                  <Button
                    danger
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => handleAcceptOrder("Cancelled")}
                  >
                    Cancel
                  </Button>
                </Tooltip>
              </Space>
            }
            style={{ borderRadius: 8 }}
          >
            {/* Assign Driver */}
            <Card
              size="small"
              title="Assign Driver"
              extra={
                <Select
                  options={drivers}
                  value={
                    selectedOrder?.asign_driver
                      ? safeJSON(selectedOrder.asign_driver, {})?.id
                      : undefined
                  }
                  placeholder="Select a driver"
                  style={{ width: 240 }}
                  onChange={(_, opt) => handleAssignDriver(opt.fullData)}
                  allowClear
                />
              }
              style={{ marginBottom: 16 }}
            >
              {renderDriver()}
            </Card>

            <Divider style={{ margin: "0 0 16px 0" }} />

            {/* Scrollable detail sections */}
            <div style={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto", paddingRight: 4 }}>
              {renderDetails()}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OrderPage;